/**
 * Bottom Navigation Component - Trade Loop v2.0
 *
 * Mobile-first bottom navigation with 5 items:
 * Home, Referrals, Send Referral (FAB), Network, Settings
 *
 * Migrated from v1.0 with React Router DOM
 */

import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Home, ArrowLeftRight, Users, Settings, Plus, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyboardInset } from "@/hooks/useKeyboardInset";

interface BottomNavigationProps {
  activeTab?: string;
}

export default function BottomNavigation({ activeTab }: BottomNavigationProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { isKeyboardVisible } = useKeyboardInset();

  const isAdmin = user?.isAdmin;

  // Check if user is an organization member (not admin/owner)
  const isOrgMember = user?.organizationMembership?.role === 'member';

  // Enhanced tab detection logic that handles route variations
  const currentTab =
    activeTab ||
    (() => {
      const path = location.pathname.toLowerCase();

      // Dashboard routes (includes organization page for org members)
      if (path === "/" || path === "/dashboard" || path === "/home" ||
          (isOrgMember && path === "/organization")) {
        return "dashboard";
      }

      // Referrals routes
      if (path === "/referrals" || path.startsWith("/referral")) {
        return "referrals";
      }

      // Network routes
      if (
        path === "/network" ||
        path === "/partners" ||
        path.startsWith("/network")
      ) {
        return "network";
      }

      // Admin routes
      if (path === "/admin" || path.startsWith("/admin")) {
        return "admin";
      }

      // Profile/Settings routes
      if (
        path === "/profile" ||
        path === "/settings" ||
        path === "/edit-profile" ||
        path.startsWith("/profile") ||
        path.startsWith("/settings")
      ) {
        return "profile";
      }

      // Default to dashboard for unknown routes
      return "dashboard";
    })();

  const navItems = [
    {
      id: "dashboard",
      label: "Home",
      icon: Home,
      href: isOrgMember ? "/organization" : "/dashboard",
    },
    {
      id: "referrals",
      label: "Referrals",
      icon: ArrowLeftRight,
      href: "/referrals",
    },
    {
      id: "network",
      label: "Network",
      icon: Users,
      href: "/network",
    },
    {
      id: "profile",
      label: "Settings",
      icon: Settings,
      href: "/settings",
    },
  ];

  if (isKeyboardVisible) return null;

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 z-50 shadow-lg pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-md mx-auto grid grid-cols-5 items-center px-2 py-1">
          {/* Home - Column 1 */}
          <div className="flex justify-center">
            {(() => {
              const item = navItems[0];
              const isActive = currentTab === item.id;
              const Icon = item.icon;

              return (
                <Link to={item.href}>
                  <button
                    className={cn(
                      "flex flex-col items-center justify-center space-y-1 py-2 px-2 transition-all duration-300 min-w-[60px] min-h-[52px] rounded-xl group",
                      isActive
                        ? "text-blue-600 bg-blue-50 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5 transition-transform duration-200",
                        isActive && "scale-110"
                      )}
                    />
                    <span
                      className={cn(
                        "text-xs font-medium transition-colors",
                        isActive
                          ? "text-blue-600"
                          : "text-gray-600 group-hover:text-gray-900"
                      )}
                    >
                      {item.label}
                    </span>
                  </button>
                </Link>
              );
            })()}
          </div>

          {/* Referrals - Column 2 */}
          <div className="flex justify-center">
            {(() => {
              const item = navItems[1];
              const isActive = currentTab === item.id;
              const Icon = item.icon;

              return (
                <Link to={item.href}>
                  <button
                    className={cn(
                      "flex flex-col items-center justify-center space-y-1 py-2 px-2 transition-all duration-300 min-w-[60px] min-h-[52px] rounded-xl group",
                      isActive
                        ? "text-blue-600 bg-blue-50 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5 transition-transform duration-200",
                        isActive && "scale-110"
                      )}
                    />
                    <span
                      className={cn(
                        "text-xs font-medium transition-colors",
                        isActive
                          ? "text-blue-600"
                          : "text-gray-600 group-hover:text-gray-900"
                      )}
                    >
                      {item.label}
                    </span>
                  </button>
                </Link>
              );
            })()}
          </div>

          {/* Column 3: Send Referral FAB (non-admin) or Admin button (admin) */}
          <div className="flex justify-center items-center">
            {isAdmin ? (
              // Admin button for admin users
              <Link to="/admin">
                <button
                  className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 -mt-2 group",
                    currentTab === "admin"
                      ? "bg-gradient-to-br from-purple-600 to-purple-700"
                      : "bg-gradient-to-br from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                  )}
                  aria-label="Admin panel"
                >
                  <Shield className="w-6 h-6 text-white transition-transform duration-200 group-hover:scale-110" />
                </button>
              </Link>
            ) : (
              // Send Referral FAB for regular users
              <Link to={`/send-referral?returnTo=${encodeURIComponent(location.pathname)}`}>
                <button
                  data-tour-target="nav-plus"
                  className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 -mt-2 group"
                  aria-label="Send referral"
                >
                  <Plus className="w-6 h-6 text-white transition-transform duration-200 group-hover:scale-110" />
                </button>
              </Link>
            )}
          </div>

          {/* Network - Column 4 */}
          <div className="flex justify-center">
            {(() => {
              const item = navItems[2];
              const isActive = currentTab === item.id;
              const Icon = item.icon;

              // If org member, render disabled button without Link
              if (isOrgMember) {
                return (
                  <button
                    disabled
                    className={cn(
                      "flex flex-col items-center justify-center space-y-1 py-2 px-2 transition-all duration-300 min-w-[60px] min-h-[52px] rounded-xl cursor-not-allowed opacity-40"
                    )}
                    title="Network features are managed by your organization administrator"
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium text-gray-400">
                      {item.label}
                    </span>
                  </button>
                );
              }

              return (
                <Link to={item.href} data-tour-target="nav-network">
                  <button
                    className={cn(
                      "flex flex-col items-center justify-center space-y-1 py-2 px-2 transition-all duration-300 min-w-[60px] min-h-[52px] rounded-xl group",
                      isActive
                        ? "text-blue-600 bg-blue-50 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5 transition-transform duration-200",
                        isActive && "scale-110"
                      )}
                    />
                    <span
                      className={cn(
                        "text-xs font-medium transition-colors",
                        isActive
                          ? "text-blue-600"
                          : "text-gray-600 group-hover:text-gray-900"
                      )}
                    >
                      {item.label}
                    </span>
                  </button>
                </Link>
              );
            })()}
          </div>

          {/* Settings - Column 5 */}
          <div className="flex justify-center">
            {(() => {
              const item = navItems[3];
              const isActive = currentTab === item.id;
              const Icon = item.icon;

              return (
                <Link to={item.href}>
                  <button
                    className={cn(
                      "flex flex-col items-center justify-center space-y-1 py-2 px-2 transition-all duration-300 min-w-[60px] min-h-[52px] rounded-xl group",
                      isActive
                        ? "text-blue-600 bg-blue-50 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5 transition-transform duration-200",
                        isActive && "scale-110"
                      )}
                    />
                    <span
                      className={cn(
                        "text-xs font-medium transition-colors",
                        isActive
                          ? "text-blue-600"
                          : "text-gray-600 group-hover:text-gray-900"
                      )}
                    >
                      {item.label}
                    </span>
                  </button>
                </Link>
              );
            })()}
          </div>
        </div>
      </nav>
    </>
  );
}
